import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { Add20Regular, Delete20Regular, Search20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

// models
import {
  IdentifierTypeCreateCommand,
  IdentifierTypeViewModel
} from '../../../models/Entities/Person/IdentifierType/IdentifierTypeModels';

// components
import IdentifierTypeFormComponent from './IdentifierType.Form.Component';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import {
  GuideRowActions,
  GuideTable,
  GuideTh,
  guideCellClassName as cell,
  guideRowClassName,
  useGuideSort
} from '../../../theme/components/GuideTable';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ComponentFunctions from './IdentifierTypes.List.Functions';
import { IdentifierTypeSavedMode } from './IdentifierType.Form.Functions';
import {
  CUIT_PREFILL,
  filterIdentifierTypes,
  findCuitTypes,
  identifierTypeErrorKind,
  identifierTypeErrorMessage,
  inUseCountOf,
  isCuitName,
  isForNaturalPersonOf,
  toStatusCommand
} from './IdentifierTypes.Functions';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import './IdentifierTypes.css';

interface ActionError {
  title: string;
  detail: string;
}

interface FormState {
  open: boolean;
  identifierTypeId?: number;
  prefill?: Partial<IdentifierTypeCreateCommand>;
}

/**
 * Tipos de identificador de personas (spec 011), en "Configuraciones adicionales": grilla de la guía con
 * buscador, estado con texto, acciones en ⋯ (Editar, Activar / Desactivar, Eliminar con confirmación), aviso
 * con "Crear CUIT" si falta el tipo que usan los proveedores y advertencia si hay varios.
 */
const IdentifierTypesListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();
  const tl = (key: string, options?: Record<string, unknown>) => String(t(`persons.identifierTypes.list.${key}`, options));

  const [search, setSearch] = useState<string>('');
  const [form, setForm] = useState<FormState>({ open: false });
  const [notice, setNotice] = useState<string>('');
  const [togglingId, setTogglingId] = useState<number | undefined>(undefined);
  const [actionError, setActionError] = useState<ActionError | undefined>(undefined);
  const [deleting, setDeleting] = useState<IdentifierTypeViewModel | undefined>(undefined);
  const [removing, setRemoving] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

  useEffect(() => {
    props.GetIdentifierTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const items = props.identifierTypes ?? [];
  const cuitTypes = findCuitTypes(items);
  const filtered = filterIdentifierTypes(items, search);
  const { rows, sortProps } = useGuideSort(
    filtered,
    {
      name: (item: IdentifierTypeViewModel) => item.name ?? '',
      juridiction: (item: IdentifierTypeViewModel) => juridictionText(item),
      status: (item: IdentifierTypeViewModel) => (item.isActive ? 0 : 1)
    },
    { key: 'name', direction: 'asc' }
  );

  function juridictionText(item: IdentifierTypeViewModel): string {
    const option = CommonFunctions.JuridictionTypeOptions.find((o) => o.key === item.juridictionType);
    return option ? String(t(option.textKey, { defaultValue: option.defaultText })) : '—';
  }

  const appliesToText = (item: IdentifierTypeViewModel): string =>
    [
      isForNaturalPersonOf(item) ? String(t('persons.personType.natural', { defaultValue: 'Persona física' })) : undefined,
      item.isForJuridicalPerson ? String(t('persons.personType.juridical', { defaultValue: 'Persona jurídica' })) : undefined
    ]
      .filter((value): value is string => !!value)
      .join(' · ');

  const openCreate = (prefill?: Partial<IdentifierTypeCreateCommand>) => setForm({ open: true, prefill });
  const openEdit = (item: IdentifierTypeViewModel) => setForm({ open: true, identifierTypeId: item.id });
  const closeForm = () => setForm({ open: false });

  const onSaved = (name: string, mode: IdentifierTypeSavedMode) => setNotice(tl(mode, { name }));

  /** 404 de Persons.API (spec 005): otro usuario lo eliminó; quien lo detecta recarga la lista. */
  const notFoundError = (name: string): ActionError => ({ title: tl('notFoundTitle'), detail: tl('notFound', { name }) });

  const toggleStatus = async (item: IdentifierTypeViewModel) => {
    const isActive = !item.isActive;
    setTogglingId(item.id);
    setActionError(undefined);
    try {
      await props.ChangeIdentifierTypeStatus(item.id, isActive, toStatusCommand(item, isActive));
      setNotice(tl(isActive ? 'activated' : 'deactivated', { name: item.name ?? '' }));
    } catch (error: unknown) {
      if (identifierTypeErrorKind(error) === 'notFound') {
        props.GetIdentifierTypes();
        setActionError(notFoundError(item.name ?? ''));
      } else {
        setActionError({ title: tl('statusError'), detail: identifierTypeErrorMessage(error) });
      }
    } finally {
      setTogglingId(undefined);
    }
  };

  const askDelete = (item: IdentifierTypeViewModel) => {
    setDeleteError(undefined);
    setDeleting(item);
  };

  const confirmDelete = async () => {
    const item = deleting;
    if (!item) return;
    setRemoving(true);
    setDeleteError(undefined);
    try {
      await props.DeleteIdentifierType(item.id);
      setDeleting(undefined);
      setNotice(tl('deleted', { name: item.name ?? '' }));
    } catch (error: unknown) {
      const kind = identifierTypeErrorKind(error);
      if (kind === 'notFound') {
        // Ya no existe (Persons.API 005 responde 404; antes, 200 con succeeded false): se cierra y se recarga.
        setDeleting(undefined);
        props.GetIdentifierTypes();
        setActionError(notFoundError(item.name ?? ''));
      } else if (kind === 'inUse') {
        setDeleteError(tl('deleteInUse', { count: inUseCountOf(error) }));
      } else {
        setDeleteError(identifierTypeErrorMessage(error));
      }
    } finally {
      setRemoving(false);
    }
  };

  const loaded = !!props.isLoadingSuccessfully || !!props.failOnLoading;
  const loadFailed = !!props.failOnLoading && !props.isLoading;

  const loadErrorBar = (
    <GuideMessageBar intent="error" layout="multiline" data-testid="idt-load-error">
      <MessageBarBody>
        <MessageBarTitle>{tl('error')}</MessageBarTitle>
        {getApiErrorMessage(props.error)}
      </MessageBarBody>
      <MessageBarActions>
        <GuideBannerAction onClick={() => props.GetIdentifierTypes()}>{tl('retry')}</GuideBannerAction>
      </MessageBarActions>
    </GuideMessageBar>
  );

  const renderBody = () => {
    if (loadFailed && items.length === 0) return loadErrorBar;
    if (!loaded || (props.isLoading && items.length === 0)) {
      return <GuideSkeleton rows={3} label={tl('loading')} data-testid="idt-loading" />;
    }
    if (items.length === 0) {
      return (
        <GuideEmptyState
          title={tl('emptyTitle')}
          text={tl('emptyText')}
          data-testid="idt-empty"
          action={
            <Button appearance="primary" icon={<Add20Regular />} onClick={() => openCreate()}>
              {tl('add')}
            </Button>
          }
        />
      );
    }
    if (rows.length === 0) {
      return <GuideEmptyState title={tl('noMatchTitle')} text={tl('noMatchText')} data-testid="idt-no-match" />;
    }
    return (
      <GuideTable ariaLabel={tl('tableLabel')} data-testid="idt-table">
        <thead>
          <tr>
            <GuideTh sort={sortProps('name')}>{tl('name')}</GuideTh>
            <GuideTh sort={sortProps('juridiction')}>{tl('juridictionType')}</GuideTh>
            <GuideTh>{tl('appliesTo')}</GuideTh>
            <GuideTh>{tl('mask')}</GuideTh>
            <GuideTh sort={sortProps('status')}>{tl('status')}</GuideTh>
            <th className={cell('actions')} scope="col">
              <span className="idt-sr">{tl('actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody aria-busy={props.isLoading || undefined}>
          {rows.map((item) => {
            const applies = appliesToText(item);
            return (
              <tr key={item.id} className={guideRowClassName({ inactive: !item.isActive })} data-testid={`idt-row-${item.id}`}>
                <td>
                  <span className="idt-name">{item.name || '—'}</span>
                  {item.description && <span className="idt-description">{item.description}</span>}
                </td>
                <td>{juridictionText(item)}</td>
                <td>{applies || <span className="idt-secondary">{tl('none')}</span>}</td>
                <td className="idt-mono">{item.identifierMask || <span className="idt-secondary">—</span>}</td>
                <td>
                  <StatusPill tone={item.isActive ? 'success' : 'neutral'} data-testid={`idt-status-${item.id}`}>
                    {item.isActive ? tl('active') : tl('inactive')}
                  </StatusPill>
                </td>
                <td className={cell('actions')} data-testid={`idt-actions-${item.id}`}>
                  <GuideRowActions
                    label={tl('rowActions', { name: item.name ?? '' })}
                    busy={togglingId === item.id}
                    actions={[
                      { key: 'edit', label: tl('edit'), onClick: () => openEdit(item) },
                      {
                        key: 'status',
                        label: item.isActive ? tl('deactivate') : tl('activate'),
                        onClick: () => toggleStatus(item)
                      },
                      { key: 'delete', label: tl('delete'), danger: true, onClick: () => askDelete(item) }
                    ]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </GuideTable>
    );
  };

  return (
    <div className="idt-section" data-testid="idt-section">
      <p className="idt-hint">{tl('cuitHint')}</p>

      {loaded && !props.isLoading && !loadFailed && cuitTypes.length === 0 && (
        <GuideMessageBar intent="info" layout="multiline" data-testid="idt-cuit-missing">
          <MessageBarBody>
            <MessageBarTitle>{tl('cuitMissingTitle')}</MessageBarTitle>
            {tl('cuitMissingText')}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => openCreate(CUIT_PREFILL)} data-testid="idt-create-cuit">
              {tl('createCuit')}
            </GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {cuitTypes.length > 1 && (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="idt-cuit-duplicate">
          <MessageBarBody>
            <MessageBarTitle>{tl('cuitDuplicateTitle', { count: cuitTypes.length })}</MessageBarTitle>
            {tl('cuitDuplicateText')}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      <div className="idt-toolbar">
        <Button appearance="primary" icon={<Add20Regular />} onClick={() => openCreate()} data-testid="idt-add">
          {tl('add')}
        </Button>
        <Input
          type="search"
          className="idt-search"
          contentBefore={<Search20Regular />}
          value={search}
          maxLength={100}
          onChange={(_event, data) => setSearch(data.value)}
          aria-label={tl('search')}
          placeholder={tl('searchPlaceholder')}
          data-testid="idt-search"
        />
      </div>

      <div className="idt-notice" role="status" aria-live="polite" data-testid="idt-notice">
        {notice}
      </div>

      {actionError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="idt-action-error">
          <MessageBarBody>
            <MessageBarTitle>{actionError.title}</MessageBarTitle>
            {actionError.detail}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {loadFailed && items.length > 0 && loadErrorBar}

      {renderBody()}

      <IdentifierTypeFormComponent
        isOpen={form.open}
        dismissPanel={closeForm}
        identifierTypeId={form.identifierTypeId}
        prefill={form.prefill}
        onSaved={onSaved}
        onNotFound={(name: string) => setActionError(notFoundError(name))}
      />

      <Dialog open={!!deleting} modalType="alert" onOpenChange={(_event, data) => !data.open && !removing && setDeleting(undefined)}>
        <DialogSurface data-testid="idt-delete-dialog">
          <DialogBody>
            <DialogTitle>{tl('deleteTitle')}</DialogTitle>
            <DialogContent>
              <div className="idt-dialog-stack">
                <p>{tl('deleteText', { name: deleting?.name ?? '' })}</p>
                {deleting && isCuitName(deleting.name) && (
                  <GuideMessageBar intent="warning" layout="multiline" data-testid="idt-delete-cuit-warning">
                    <MessageBarBody>{tl('deleteCuitWarning')}</MessageBarBody>
                  </GuideMessageBar>
                )}
                {deleteError !== undefined && (
                  <GuideMessageBar intent="error" layout="multiline" data-testid="idt-delete-error">
                    <MessageBarBody>
                      <MessageBarTitle>{tl('deleteError')}</MessageBarTitle>
                      {deleteError}
                    </MessageBarBody>
                  </GuideMessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleting(undefined)} disabled={removing} data-testid="idt-delete-cancel">
                {tl('cancel')}
              </Button>
              <Button
                appearance="secondary"
                className="idt-danger"
                icon={<Delete20Regular />}
                onClick={confirmDelete}
                disabled={removing}
                aria-busy={removing || undefined}
                data-testid="idt-delete-confirm"
              >
                {removing ? tl('deleting') : tl('deleteConfirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(IdentifierTypesListComponent as React.ComponentType<ComponentFunctions.Props>);
